import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { StatusToneChip } from 'src/components/partner-driver/shared/StatusToneChip'
import { Button } from 'src/components/shared/Button'
import { DataRow } from 'src/components/shared/DataRow'
import { SectionCard } from 'src/components/shared/SectionCard'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import { stepStatusTitles, stepStatusTones } from 'src/constants/partnerDriver/constants'
import { StatusTone } from 'src/constants/partnerDriver/enums'
import type { PartnerDriverSearchRow } from 'src/interfaces/partnerDriver'

interface Props {
  summary: PartnerDriverSearchRow | null
  isLoading: boolean
  error: string | null
  onRetry(): void
}

/**
 * The search endpoint returns only PENDING Yardstik reports, so the card says so in its title.
 * Finished reports, linking, releasing and re-triggering are ENG-1034.
 */
const BackgroundChecksCardComponent = ({ summary, isLoading, error, onRetry }: Props) => {
  if (isLoading && !summary) {
    return <SectionCardSkeleton />
  }

  if (error) {
    return (
      <SectionCard
        title="Pending background checks"
        actions={
          <Button size="small" color="grey" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        <Typography variant="body2" css={tw`text-critical-500`}>
          Couldn&apos;t load background checks. {error}
        </Typography>
      </SectionCard>
    )
  }

  const reports = summary?.backgroundCheckReports ?? []

  return (
    <SectionCard
      title="Pending background checks"
      dataTestId="partner-driver-background-checks-card"
    >
      {reports.length === 0 ? (
        <Typography variant="body2" css={tw`text-shades-500`}>
          No pending background checks
        </Typography>
      ) : (
        <div>
          {reports.map((report) => {
            return (
              <DataRow
                key={`${report.step}-${report.applyLink}`}
                title={stepLabel(report.step)}
                component={
                  <div css={tw`flex flex-row flex-wrap items-center justify-end gap-3`}>
                    <StatusToneChip
                      tone={stepStatusTones[report.stepStatus] ?? StatusTone.Neutral}
                      text={stepStatusTitles[report.stepStatus] ?? report.stepStatus}
                    />
                    <Typography variant="body2" css={tw`text-shades-600`}>
                      Report: {report.reportStatus}
                    </Typography>
                    {report.applyLink ? (
                      <a
                        href={report.applyLink}
                        target="_blank"
                        rel="noreferrer"
                        css={tw`text-primary-600`}
                      >
                        Open apply link
                      </a>
                    ) : null}
                  </div>
                }
              />
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}

export const BackgroundChecksCard = memo(BackgroundChecksCardComponent)
